import React, { useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { CheckCircle2, ArrowLeft, BadgeCheck, X, Quote, ArrowRight, MessageCircle, Lock } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { getPluginIcon } from '../components/PluginCard';
import { getPlugin } from '../data/plugins';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const hexToRgba = (hex, a) => {
  const h = hex.replace('#', '');
  const n = parseInt(h, 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${a})`;
};

const PluginDetailPage = () => {
  const { slug } = useParams();
  const plugin = getPlugin(slug);
  const { user, isMember, login } = useAuth();
  const navigate = useNavigate();
  const [demoOpen, setDemoOpen] = useState(false);
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [creatorBody, setCreatorBody] = useState('');
  const [creatorSent, setCreatorSent] = useState(false);
  const [creatorSending, setCreatorSending] = useState(false);
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState(null);

  if (!plugin) return <Navigate to="/plugins" replace />;

  const Icon = getPluginIcon(plugin.icon);
  const accent = plugin.accent || '#334155';

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) { setError('Please enter a valid email'); return; }
    setSubmitting(true); setError('');
    const API = process.env.REACT_APP_BACKEND_URL;
    try {
      await fetch(`${API}/api/events/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event_type: 'plugin_demo_request', email: trimmed, plugin: plugin.name }),
      });
    } catch (_) { /* best-effort */ }
    setSubmitting(false); setSubmitted(true);
  };

  const closeDemo = () => { setDemoOpen(false); setSubmitted(false); setEmail(''); setError(''); };

  const openCreator = () => { setCreatorSent(false); setCreatorBody(''); setCreatorOpen(true); };
  const sendCreator = async () => {
    if (!creatorBody.trim()) return;
    setCreatorSending(true);
    try {
      const res = await fetch(`${API}/api/plugins/${plugin.slug}/message`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ body: creatorBody.trim(), creator: plugin.author }),
      });
      if (res.ok) setCreatorSent(true);
    } catch (_) { /* ignore */ }
    setCreatorSending(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title={`${plugin.name}, Forsyt Plugin`} description={plugin.tagline} path={`/plugins/${plugin.slug}`} />
      <Header />

      <main className="pb-20">
        {/* Hero band */}
        <section
          className="pt-12 pb-12 border-b border-slate-100"
          style={{ background: `linear-gradient(180deg, ${hexToRgba(accent, 0.06)}, #ffffff)` }}
        >
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <Link to="/plugins" data-testid="plugin-detail-back"
              className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Plugin Store
            </Link>

            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-8">
              <div className="max-w-3xl">
                <div className="flex flex-wrap items-center gap-3 mb-5">
                  <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-white shadow-sm ring-1 ring-slate-900/5" style={{ color: accent }}>
                    <Icon className="w-6 h-6" strokeWidth={1.9} />
                  </span>
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">{plugin.category}</div>
                    <div className="flex items-center gap-2 text-[11px] text-slate-500 font-mono">
                      v{plugin.version}
                      {plugin.verified && (<span className="inline-flex items-center gap-1 text-slate-600 font-sans not-italic"><BadgeCheck className="w-3 h-3" /> Verified by Forsyt</span>)}
                    </div>
                  </div>
                  <span className="ml-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full border border-slate-200 text-slate-600 bg-white">
                    {plugin.status}
                  </span>
                </div>

                <h1 className="text-5xl md:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.03] mb-4">
                  {plugin.name}
                </h1>
                <p className="text-xl text-slate-600 leading-relaxed max-w-2xl">{plugin.tagline}</p>

                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <button onClick={() => setDemoOpen(true)} data-testid="plugin-demo-open"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-white text-sm font-semibold transition-opacity hover:opacity-90"
                    style={{ backgroundColor: accent }}>
                    Request a demo
                  </button>
                  <Link to="/plugins" data-testid="plugin-back-to-store"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-slate-900 bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50 transition-colors">
                    Back to store
                  </Link>
                  <span className="text-sm text-slate-500 ml-1"><strong className="text-slate-700">{plugin.pricing}</strong></span>
                </div>
              </div>

              {/* Requested-by card */}
              <div className="lg:w-80 shrink-0 bg-white border border-slate-200 rounded-2xl p-6" data-testid="plugin-requested-by">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Requested by</div>
                <p className="text-slate-900 font-semibold leading-snug">A {plugin.requested_by.designation}</p>
                <p className="text-sm text-slate-600 leading-snug mt-1">at {plugin.requested_by.company}</p>
                <p className="text-xs text-slate-400 mt-1">{plugin.requested_by.region}</p>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 text-slate-500">
                    <BadgeCheck className="w-3.5 h-3.5" />
                  </span>
                  {plugin.author_role}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-1">Creator</div>
                  <p className="text-sm font-semibold text-slate-900">{plugin.author}</p>
                  <button
                    onClick={openCreator}
                    data-testid="plugin-message-creator"
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: accent }}
                  >
                    {isMember ? <MessageCircle className="w-4 h-4" /> : <Lock className="w-4 h-4" />} Message the creator
                  </button>
                  {!isMember && <p className="text-[11px] text-slate-400 mt-2 text-center">A Revenue Community perk</p>}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Requester story */}
        {plugin.requested_context && (
          <section className="py-14 border-b border-slate-100">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
              <div className="flex items-start gap-4">
                <Quote className="w-8 h-8 shrink-0 mt-1" style={{ color: accent }} />
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">Why this exists</div>
                  <p className="text-xl md:text-2xl text-slate-800 leading-relaxed font-medium tracking-tight" data-testid="plugin-requester-story">
                    {plugin.requested_context}
                  </p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Screenshots / preview */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            {plugin.screenshots && plugin.screenshots.length > 0 ? (
              <div className="space-y-14" data-testid="plugin-screenshots">
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Inside {plugin.name}</div>
                {plugin.screenshots.map((s, i) => (
                  <figure key={i} className={`grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-center ${i % 2 === 1 ? 'lg:[direction:rtl]' : ''}`}>
                    <div className="lg:col-span-7 [direction:ltr]">
                      <button
                        onClick={() => setLightbox(s.src)}
                        data-testid={`plugin-screenshot-${i}`}
                        className="block w-full rounded-2xl overflow-hidden border border-slate-200 shadow-[0_20px_60px_-24px_rgba(15,23,42,0.35)] hover:shadow-[0_28px_70px_-24px_rgba(15,23,42,0.45)] transition-shadow"
                      >
                        <img src={s.src} alt={`${plugin.name} — ${s.title}`} loading="lazy" className="w-full block" />
                      </button>
                    </div>
                    <figcaption className="lg:col-span-5 [direction:ltr]">
                      <div className="text-2xl font-bold text-slate-900 tracking-tight mb-2 flex items-center gap-3">
                        <span className="text-sm font-mono px-2 py-1 rounded-md" style={{ backgroundColor: hexToRgba(accent, 0.1), color: accent }}>
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        {s.title}
                      </div>
                      <p className="text-slate-600 leading-relaxed">{s.caption}</p>
                    </figcaption>
                  </figure>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/60 p-12 text-center" data-testid="plugin-screenshots-empty">
                <div className="text-sm font-semibold text-slate-700">Product walkthrough coming soon</div>
                <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
                  {plugin.name} is in {plugin.status.toLowerCase()}. Request a demo to see it live on your own data.
                </p>
                <button onClick={() => setDemoOpen(true)} data-testid="plugin-empty-demo"
                  className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ backgroundColor: accent }}>
                  Request a demo <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </section>

        {/* Body */}
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2 space-y-14">
              <section data-testid="plugin-overview">
                <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">Overview</h2>
                <p className="text-slate-700 leading-relaxed text-base">{plugin.short}</p>
              </section>

              <section data-testid="plugin-why">
                <h2 className="text-2xl font-bold text-slate-900 mb-3 tracking-tight">Why we built this</h2>
                <p className="text-slate-700 leading-relaxed text-base">{plugin.why}</p>
              </section>

              {plugin.use_case && plugin.use_case.length > 0 && (
                <section data-testid="plugin-usecase">
                  <h2 className="text-2xl font-bold text-slate-900 mb-6 tracking-tight">How it works</h2>
                  <ol className="space-y-5">
                    {plugin.use_case.map((u) => (
                      <li key={u.step} className="flex gap-5">
                        <span className="shrink-0 inline-flex items-center justify-center w-11 h-11 rounded-xl text-sm font-bold font-mono"
                          style={{ backgroundColor: hexToRgba(accent, 0.1), color: accent }}>
                          {u.step}
                        </span>
                        <div className="pt-0.5">
                          <div className="text-lg font-semibold text-slate-900">{u.title}</div>
                          <p className="text-slate-600 leading-relaxed mt-1">{u.body}</p>
                        </div>
                      </li>
                    ))}
                  </ol>
                </section>
              )}

              <section data-testid="plugin-highlights">
                <h2 className="text-2xl font-bold text-slate-900 mb-5 tracking-tight">Capabilities</h2>
                <ul className="border border-slate-200 rounded-2xl divide-y divide-slate-100">
                  {plugin.highlights.map((h, i) => (
                    <li key={i} className="flex items-start gap-3 px-5 py-4">
                      <CheckCircle2 className="w-4 h-4 shrink-0 mt-1" strokeWidth={2.2} style={{ color: accent }} />
                      <span className="text-sm text-slate-700 leading-relaxed">{h}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {plugin.sources && plugin.sources.length > 0 && (
                <section data-testid="plugin-sources">
                  <h2 className="text-2xl font-bold text-slate-900 mb-5 tracking-tight">Sources</h2>
                  <p className="text-sm text-slate-600 leading-relaxed mb-4">
                    {plugin.name} draws on the sources below. Nothing is a black box.
                  </p>
                  <ul className="divide-y divide-slate-100 border border-slate-200 rounded-2xl">
                    {plugin.sources.map((s) => (
                      <li key={s} className="px-5 py-3 text-sm text-slate-700 font-mono">{s}</li>
                    ))}
                  </ul>
                </section>
              )}
            </div>

            {/* Right rail */}
            <aside className="space-y-6">
              <div className="bg-white border rounded-2xl p-6 text-center" style={{ borderColor: hexToRgba(accent, 0.3), backgroundColor: hexToRgba(accent, 0.04) }} data-testid="plugin-demo-rail">
                <div className="text-sm font-bold text-slate-900">See {plugin.name} on your data</div>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">A 20-minute walkthrough with a real list of yours.</p>
                <button onClick={() => setDemoOpen(true)} data-testid="plugin-demo-open-rail"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl text-white text-sm font-semibold transition-opacity hover:opacity-90"
                  style={{ backgroundColor: accent }}>
                  Request a demo
                </button>
              </div>

              <RailBlock title="Inputs" testid="plugin-meta-inputs">
                <ul className="space-y-2">{plugin.inputs.map((x) => <li key={x} className="text-sm text-slate-700 leading-snug">{x}</li>)}</ul>
              </RailBlock>
              <RailBlock title="Outputs" testid="plugin-meta-outputs">
                <ul className="space-y-2">{plugin.outputs.map((x) => <li key={x} className="text-sm text-slate-700 leading-snug">{x}</li>)}</ul>
              </RailBlock>
              <RailBlock title="Works with" testid="plugin-meta-works-with">
                <div className="flex flex-wrap gap-2">
                  {plugin.works_with.map((x) => <span key={x} className="inline-block text-xs text-slate-700 bg-slate-100 border border-slate-200 px-2.5 py-1 rounded-md">{x}</span>)}
                </div>
              </RailBlock>
              <RailBlock title="Performance" testid="plugin-meta-performance">
                <ul className="space-y-3">
                  {plugin.performance.map((p) => (
                    <li key={p.label}>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{p.label}</div>
                      <div className="text-sm font-semibold text-slate-900 mt-0.5">{p.value}</div>
                    </li>
                  ))}
                </ul>
              </RailBlock>
              <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="plugin-meta-scopes">
                <div className="flex items-center justify-between mb-3">
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Scopes requested</div>
                  <span className="text-[9px] font-bold uppercase tracking-wider text-slate-700 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-full">Minimal</span>
                </div>
                <ul className="space-y-1.5">
                  {plugin.scopes.map((s) => (
                    <li key={s} className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 px-2.5 py-1.5 rounded-md">{s}</li>
                  ))}
                </ul>
                <p className="text-[11px] text-slate-500 leading-relaxed mt-3">
                  Runs in an isolated sandbox. Receives typed bands and hashed IDs, never raw records.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-[210] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-10" onClick={() => setLightbox(null)} data-testid="plugin-lightbox">
          <button className="absolute right-5 top-5 text-white/80 hover:text-white" onClick={() => setLightbox(null)} aria-label="Close"><X className="w-7 h-7" /></button>
          <img src={lightbox} alt="Plugin screenshot" className="max-w-full max-h-full rounded-lg shadow-2xl" onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Demo request modal */}
      {demoOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={closeDemo} data-testid="plugin-demo-modal">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-7" onClick={(e) => e.stopPropagation()}>
            <button onClick={closeDemo} className="absolute right-4 top-4 text-slate-400 hover:text-slate-900" data-testid="plugin-demo-close" aria-label="Close"><X className="w-5 h-5" /></button>
            {!submitted ? (
              <>
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">{plugin.category}</div>
                <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">Request a demo of {plugin.name}</h2>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">
                  Drop your work email and we will set up a short walkthrough, ideally on a real list or territory of yours. Usually within a day.
                </p>
                <form onSubmit={handleDemoSubmit} className="space-y-3">
                  <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setError(''); }} placeholder="name@company.com" autoFocus data-testid="plugin-demo-email"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 placeholder:text-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/20 focus:border-slate-400 transition-all" />
                  {error && <p className="text-red-500 text-xs" data-testid="plugin-demo-error">{error}</p>}
                  <button type="submit" disabled={submitting} data-testid="plugin-demo-submit"
                    className="w-full px-6 py-3 text-white text-sm font-semibold rounded-xl transition-opacity hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: accent }}>
                    {submitting ? 'Sending...' : 'Request a demo'}
                  </button>
                </form>
                <p className="text-[11px] text-slate-400 text-center mt-3">We only use this email to follow up on your request.</p>
              </>
            ) : (
              <div className="text-center py-2" data-testid="plugin-demo-submitted">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                  <svg className="w-6 h-6 text-emerald-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-1">Request taken.</h2>
                <p className="text-sm text-slate-500 leading-relaxed mb-5">Thank you. We will be in touch shortly to schedule your {plugin.name} demo.</p>
                <button onClick={closeDemo} data-testid="plugin-demo-done" className="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all">Close</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Message-the-creator modal (gated by community membership) */}
      {creatorOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4" onClick={() => setCreatorOpen(false)} data-testid="plugin-creator-modal">
          <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl p-7" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setCreatorOpen(false)} className="absolute right-4 top-4 text-slate-400 hover:text-slate-900" aria-label="Close"><X className="w-5 h-5" /></button>
            {isMember ? (
              !creatorSent ? (
                <>
                  <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Message the creator</div>
                  <h2 className="text-xl font-extrabold text-slate-900 tracking-tight mb-1">{plugin.author}</h2>
                  <p className="text-sm text-slate-600 mb-4">Ask about {plugin.name} — how it works, roadmap, or getting early access.</p>
                  <textarea value={creatorBody} onChange={(e) => setCreatorBody(e.target.value)} rows={4} placeholder={`Hi, about ${plugin.name}…`} data-testid="plugin-creator-body"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900/20" />
                  <button onClick={sendCreator} disabled={creatorSending} data-testid="plugin-creator-send" className="mt-3 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl text-white text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-60" style={{ backgroundColor: accent }}>
                    {creatorSending ? 'Sending…' : (<>Send message <MessageCircle className="w-4 h-4" /></>)}
                  </button>
                </>
              ) : (
                <div className="text-center py-2" data-testid="plugin-creator-sent">
                  <div className="mx-auto w-12 h-12 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mb-3"><CheckCircle2 className="w-6 h-6 text-emerald-600" /></div>
                  <h2 className="text-lg font-bold text-slate-900">Message sent</h2>
                  <p className="text-sm text-slate-500 mt-1 mb-4">The {plugin.author} team will get back to you.</p>
                  <button onClick={() => setCreatorOpen(false)} className="px-5 py-2.5 rounded-xl bg-slate-100 text-slate-700 text-sm font-semibold">Close</button>
                </div>
              )
            ) : (
              <>
                <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Community perk</div>
                <h2 className="text-xl font-extrabold text-slate-900 tracking-tight mb-2">Messaging creators is for members</h2>
                <p className="text-sm text-slate-600 leading-relaxed mb-5">Plugin creators are part of the Revenue Community. Join to message them and other revenue leaders directly.</p>
                {!user ? (
                  <button onClick={login} data-testid="plugin-creator-login" className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">Continue with Google to apply</button>
                ) : (
                  <button onClick={() => navigate('/community/apply')} className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">Complete your application <ArrowRight className="w-4 h-4" /></button>
                )}
                <button onClick={() => navigate('/community')} className="mt-2 w-full px-6 py-2.5 rounded-xl border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">See the community</button>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

const RailBlock = ({ title, testid, children }) => (
  <div className="bg-white border border-slate-200 rounded-2xl p-5" data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-3">{title}</div>
    {children}
  </div>
);

export default PluginDetailPage;
